import { ManualCostAllocation, manualAllocations } from "./CostAllocation";
import React, { useState, useEffect, useRef } from "react";
import DocumentToolbar from "../common/DocumentToolbar";
import useDocumentDraft from "../../hooks/useDocumentDraft";
import { useNavigate } from "react-router-dom";
import { Container, Card, Form, Row, Col, Table, Button, Alert, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { authFetch } from "../utils/authFetch";
import AccountPickerModal from "../pickers/AccountPickerModal";
import ClientPickerModal from "../pickers/ClientPickerModal";

export default function NcCreate() {
  const formElement = useRef(null);
  const navigate = useNavigate();

  // Master State
  const [types, setTypes] = useState([]);
  const [recType, setRecType] = useState("");
  const [docNo, setDocNo] = useState("");
  const [dataNc, setDataNc] = useState(new Date().toISOString().substring(0, 10));
  const [bxexplic, setBxexplic] = useState("");

  // Lines State
  const [lines, setLines] = useState([
    { debitAccount: null, creditAccount: null, amount: "", bxexplic: "", contact: null }
  ]);

  // Modal State
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [activeLineIndex, setActiveLineIndex] = useState(null);
  const [activePickerField, setActivePickerField] = useState(null); // 'debit', 'credit'

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const draft = useDocumentDraft({ recType, docNo, dataNc, bxexplic, lines }, 'new');

  // Load Note Types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const data = await authFetch("/nc_type_cfg/");
        const rows = Array.isArray(data) ? data : (data?.items ?? []);
        setTypes(rows);
        if (rows && rows.length > 0) {
          setRecType(rows[0].rec_type);
        }
      } catch (e) {
        console.error("Error loading note types:", e);
        setError("Nu s-au putut încărca tipurile de note contabile.");
      }
    };
    fetchTypes();
  }, []);

  const addLine = () => {
    setLines([
      ...lines,
      { debitAccount: null, creditAccount: null, amount: "", bxexplic: "", contact: null }
    ]);
  };

  const deleteLine = (index) => {
    if (lines.length === 1) return;
    const newLines = [...lines];
    newLines.splice(index, 1);
    setLines(newLines);
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...lines];
    newLines[index][field] = value;
    setLines(newLines);
  };

  const openAccountPicker = (index, field) => {
    setActiveLineIndex(index);
    setActivePickerField(field);
    setShowAccountModal(true);
  };

  const openContactPicker = (index) => {
    setActiveLineIndex(index);
    setShowContactModal(true);
  };

  const handleAccountSelect = (account) => {
    const newLines = [...lines];
    if (activePickerField === "debit") {
      newLines[activeLineIndex].debitAccount = account;
    } else {
      newLines[activeLineIndex].creditAccount = account;
    }

    // Verify if contact is still required (if neither debit nor credit has switch 2, clear it)
    const needsContact =
      newLines[activeLineIndex].debitAccount?.bxswitch === 2 ||
      newLines[activeLineIndex].creditAccount?.bxswitch === 2;

    if (!needsContact) {
      newLines[activeLineIndex].contact = null;
    }

    setLines(newLines);
  };

  const handleContactSelect = (contact) => {
    const newLines = [...lines];
    newLines[activeLineIndex].contact = contact;
    setLines(newLines);
  };

  const calculateTotal = () => {
    return lines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!recType) {
      setError("Te rugăm să selectezi tipul notei contabile.");
      setLoading(false);
      return;
    }

    if (!docNo.trim()) {
      setError("Câmpul 'Număr document' este obligatoriu.");
      setLoading(false);
      return;
    }

    // Validate lines
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.debitAccount) {
        setError(`Linia ${i + 1}: Te rugăm să selectezi contul de Debit.`);
        setLoading(false);
        return;
      }
      if (!line.creditAccount) {
        setError(`Linia ${i + 1}: Te rugăm să selectezi contul de Credit.`);
        setLoading(false);
        return;
      }
      const amt = parseFloat(line.amount);
      if (!Number.isFinite(amt) || amt === 0) {
        setError(`Linia ${i + 1}: Suma trebuie să fie diferită de 0; pentru stornare folosiți o sumă negativă.`);
        setLoading(false);
        return;
      }

      const needsContact =
        line.debitAccount.bxswitch === 2 || line.creditAccount.bxswitch === 2;

      if (needsContact && !line.contact) {
        const accCode = line.debitAccount.bxswitch === 2 ? line.debitAccount.code : line.creditAccount.code;
        setError(`Linia ${i + 1}: Contul ${accCode} cere selectarea unui contact.`);
        setLoading(false);
        return;
      }
    }

    // Format payload
    const payload = {
      rec_type: parseInt(recType),
      doc_no: docNo,
      data_nc: dataNc,
      bxexplic: bxexplic,
      lines: lines.map((line) => {
        const needsContact =
          line.debitAccount.bxswitch === 2 || line.creditAccount.bxswitch === 2;
        return {
          account_id: line.debitAccount.id,
          accountcoresp_id: line.creditAccount.id,
          doc_lns_id: line.doc_lns_id ?? null,
          cost_alloc: manualAllocations(line),
          amount: parseFloat(line.amount),
          bxexplic: line.bxexplic || "",
          contact_id: needsContact ? line.contact.id : null
        };
      })
    };

    try {
      await authFetch("/nc/", {
        method: "POST",
        body: JSON.stringify(payload)
      });
      setSuccess(true);
      draft.saved();
      toast.success("Nota contabilă a fost salvată cu succes!");
      setTimeout(() => {
        navigate("/nc");
      }, 1500);
    } catch (err) {
      console.error(err);
      setError(err.message || "Eroare la salvarea notei contabile.");
      toast.error(`Eroare la salvare: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <DocumentToolbar title="Creează notă contabilă" dirty={draft.dirty} newDocument busy={loading} error={error}
        onBack={() => navigate('/nc')} onSave={() => formElement.current?.requestSubmit()} saveDisabled={success} />

      {error && <Alert variant="danger" className="shadow-sm">{error}</Alert>}
      {success && <Alert variant="success" className="shadow-sm">Nota contabilă a fost salvată cu succes! Redirecționare...</Alert>}

      <Form ref={formElement} onPointerDownCapture={draft.interact} onKeyDownCapture={draft.interact} onChangeCapture={draft.interact} onSubmit={handleSubmit}>
        <Card className="shadow-sm border-0 mb-4 bg-light">
          <Card.Body className="p-4">
            <h5 className="fw-bold mb-3 text-secondary">Informații Antet</h5>
            <Row className="g-3">
              <Col md={3}>
                <Form.Group controlId="recTypeSelect">
                  <Form.Label className="fw-semibold">Tip Notă</Form.Label>
                  <Form.Select
                    value={recType}
                    onChange={(e) => setRecType(e.target.value)}
                    required
                    className="border-0 shadow-sm"
                  >
                    <option value="">Alege tip...</option>
                    {types.map((t) => (
                      <option key={t.id} value={t.rec_type}>
                        {t.name} ({t.rec_type})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="docNoInput">
                  <Form.Label className="fw-semibold">Număr Document</Form.Label>
                  <Form.Control
                    type="text"
                    value={docNo}
                    onChange={(e) => setDocNo(e.target.value)}
                    required
                    placeholder="ex: NC-1001"
                    className="border-0 shadow-sm"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="dataNcInput">
                  <Form.Label className="fw-semibold">Dată Notă</Form.Label>
                  <div>
                    <DatePicker
                      selected={dataNc ? parseISO(dataNc) : null}
                      onChange={(d) => setDataNc(d ? format(d, "yyyy-MM-dd") : "")}
                      dateFormat="dd/MM/yyyy"
                      className="form-control border-0 shadow-sm"
                      wrapperClassName="w-100"
                      placeholderText="zz/mm/yyyy"
                      required
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="bxexplicInput">
                  <Form.Label className="fw-semibold">Explicație generală</Form.Label>
                  <Form.Control
                    type="text"
                    value={bxexplic}
                    onChange={(e) => setBxexplic(e.target.value)}
                    placeholder="Opțional..."
                    className="border-0 shadow-sm"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="p-4">
            <h5 className="fw-bold mb-3 text-secondary">Linii Articole</h5>
            <Table responsive borderless hover className="align-middle mb-0">
              <thead className="table-light text-secondary">
                <tr>
                  <th style={{ width: "20%" }}>Cont Debit</th>
                  <th style={{ width: "20%" }}>Cont Credit</th>
                  <th style={{ width: "15%" }}>Sumă</th>
                  <th style={{ width: "20%" }}>Explicație Linie</th>
                  <th style={{ width: "20%" }}>Contact (Partener)</th>
                  <th style={{ width: "5%" }} className="text-center">Șterge</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const needsContact =
                    line.debitAccount?.bxswitch === 2 ||
                    line.creditAccount?.bxswitch === 2;

                  return (
                    <tr key={index} className="border-bottom">
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <Button
                            variant={line.debitAccount ? "outline-dark" : "outline-primary"}
                            size="sm"
                            className="text-start border-0 bg-light shadow-sm py-2"
                            onClick={() => openAccountPicker(index, "debit")}
                          >
                            {line.debitAccount
                              ? `${line.debitAccount.code} - ${line.debitAccount.name || line.debitAccount.denumire}`
                              : "Alege cont debit..."}
                          </Button>
                          {line.debitAccount && (
                            <div className="px-1">
                              <Badge bg={line.debitAccount.bxswitch === 2 ? "warning" : "secondary"} text={line.debitAccount.bxswitch === 2 ? "dark" : "light"} style={{ fontSize: "10px" }}>
                                {line.debitAccount.bxswitch === 2 ? "Necesită partener" : "Fără evidență pe partener"}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <Button
                            variant={line.creditAccount ? "outline-dark" : "outline-primary"}
                            size="sm"
                            className="text-start border-0 bg-light shadow-sm py-2"
                            onClick={() => openAccountPicker(index, "credit")}
                          >
                            {line.creditAccount
                              ? `${line.creditAccount.code} - ${line.creditAccount.name || line.creditAccount.denumire}`
                              : "Alege cont credit..."}
                          </Button>
                          {line.creditAccount && (
                            <div className="px-1">
                              <Badge bg={line.creditAccount.bxswitch === 2 ? "warning" : "secondary"} text={line.creditAccount.bxswitch === 2 ? "dark" : "light"} style={{ fontSize: "10px" }}>
                                {line.creditAccount.bxswitch === 2 ? "Necesită partener" : "Fără evidență pe partener"}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          
                          placeholder="0.00"
                          value={line.amount}
                          onChange={(e) => handleLineChange(index, "amount", e.target.value)}
                          required
                          className="border-0 shadow-sm text-end font-monospace fw-bold py-2"
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          placeholder="Explicație..."
                          value={line.bxexplic}
                          onChange={(e) => handleLineChange(index, "bxexplic", e.target.value)}
                          className="border-0 shadow-sm py-2"
                        />
                      </td>
                      <td>
                        {needsContact ? (
                          <div className="d-flex flex-column gap-1">
                            <Button
                              variant={line.contact ? "outline-dark" : "outline-warning"}
                              size="sm"
                              className="text-start border-0 bg-light shadow-sm py-2"
                              onClick={() => openContactPicker(index)}
                            >
                              {line.contact ? line.contact.label : "Alege contact..."}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted d-block text-center py-2 bg-light rounded" style={{ fontSize: "12px" }}>
                            Nu necesită partener
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => deleteLine(index)}
                          disabled={lines.length === 1}
                          className="border-0"
                        >
                          ✕
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <ManualCostAllocation lines={lines} setLines={setLines} />

            <div className="d-flex justify-content-between align-items-center mt-3 pt-3">
              <Button variant="outline-primary" size="sm" onClick={addLine}>
                + Adaugă linie
              </Button>
              <div className="fs-5 fw-bold text-dark">
                Total sumă: <span className="font-monospace text-primary">{calculateTotal().toLocaleString("ro-RO", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </Card.Body>
        </Card>

      </Form>

      {/* Pickers Modals */}
      <AccountPickerModal
        show={showAccountModal}
        onHide={() => setShowAccountModal(false)}
        onSelect={handleAccountSelect}
        switchFilter="0,2"
      />

      <ClientPickerModal
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
        onPick={handleContactSelect}
        title="Selectează Contact (Partener)"
      />
    </Container>
  );
}

